import React, { useCallback, useEffect, useState } from "react";
import { HeaderField, InfiniteScrollList } from "./table";
import {
  getDocDetails,
  getDocHeaderById,
  updateDocDetails,
} from "../modules/api/docListsandDetails";
import { ScannerForm } from "./form";
import {
  userClientDb,
  uiLanguage,
  userShowNotCollectedUsage,
  userSearchUsage,
  refreshData,
} from "../modules/auth/selectors/userSelector";
import { useAppSelector, useAppDispatch } from "../app/hook";
import * as routes from "../app/routes";
import { useTranslation } from "react-i18next";
import { BaseLayout } from "../layouts";
import { getErrorMessage } from "./form/formUtils";
import { useAuthorizationError, useLoader } from "../hooks";
import * as modules from "../utils/modules";
import { getModuleParams } from "../modules/api/parameters";
import {
  setUserExtraFieldsArray,
  setUserBarcodeSeparator,
  setUserRowDeletion,
  setUserRefreshData,
  setUserFieldsOrder,
  setUserBarcodeSearch,
  setUserObjectSearch,
  setUserLocationSearch,
  setUserLotSearch,
  setUserObject2Search,
  setUserLot2Search,
} from "../modules/auth/slices/userSlice";
import { moduleType } from "../utils/modules";
import { Tab, Tabs, Badge, IconButton, Divider } from "@mui/material";
import {
  SearchOutlined,
  QrCodeScannerOutlined,
  Check,
  RestoreOutlined,
  CachedOutlined,
} from "@mui/icons-material";
import { TextField, Alert } from "./index";
import { useDocumentDetailsStyles } from "./css";
import { useNavigate } from "react-router-dom";

interface IDocumentDetails {
  operationCode: string;
  module: modules.moduleType;
  headerRoute: string;
}

function DocumentDetails(props: IDocumentDetails) {
  const { operationCode, module, headerRoute } = props;
  const id = operationCode ? operationCode : "";
  const showOnlyNotCollected = useAppSelector(userShowNotCollectedUsage);
  const refresh = useAppSelector(refreshData);
  const [logoutUser] = useAuthorizationError();
  const [translate] = useTranslation();
  const [data, setData] = useState();
  const [error, setError] = useState({ error: false, message: "", status: 0 });
  const userDb = useAppSelector(userClientDb);
  const locale = useAppSelector(uiLanguage);
  const useSearch = useAppSelector(userSearchUsage);
  const userDbId = userDb ? userDb.id : 0;
  const [, setLoader] = useLoader();
  const dispatch = useAppDispatch();
  const [filter, setFilter] = useState("");
  const { classes } = useDocumentDetailsStyles();
  const searchRef = React.useRef<HTMLInputElement | null>(null);
  const [paramsReady, setParamsReady] = useState(false);
  const [headerReady, setHeaderReady] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const navigate = useNavigate();
  const loadDataparams = React.useMemo<{
    operationCode: string;
    module: moduleType;
    data?: [];
    showOnlyNotCollected?: boolean;
    cellType: "header" | "info" | "item";
  }>(
    () => ({
      operationCode: id,
      module: module,
      data: data,
      showOnlyNotCollected: showOnlyNotCollected,
      cellType: "item",
      filter: filter,
    }),
    [id, module, data, showOnlyNotCollected, filter]
  );

  const [tab, setTab] = useState(0);

  const headerParams = {
    clientDbId: userDbId,
    locale: locale,
    operationCode: id,
    module: module,
  };
  const [headerData, setHeaderData] = useState({
    operationCode: "",
    operationType: "",
  });
  const moduleName = React.useMemo(() => {
    let name = "";
    let path = "/";
    switch (module) {
      case modules.MODULE_SALES:
        name = translate("sales", "Pardavimai");
        path = routes.ROUTE_SALES.path;
        break;
      case modules.MODULE_PURCHASES:
        name = translate("purchases", "Pirkimai");
        path = routes.ROUTE_PURCHASES.path;
        break;
      case modules.MODULE_INTERNAL:
        name = translate("insideAccounting", "Vidiniai");
        path = routes.ROUTE_INTERNAL.path;
        break;
      case modules.MODULE_SCANNER:
        name = translate("caches", "Kaupikliai");
        path = routes.ROUTE_SCANNER.path;
        break;
      case modules.MODULE_INVENTORY:
        name = translate("inventory", "Inventorizacija");
        path = routes.ROUTE_INVENTORY.path;
        break;
      case modules.MODULE_LOCATION:
        name = translate("locations", "Lokacijos");
        path = routes.ROUTE_LOCATION.path;
        break;
      case modules.MODULE_ASSEMBLY:
        name = translate("assembly", "Gamyba");
        path = routes.ROUTE_ASSEMBLY.path;
        break;
    }

    return { name: name, path: path };
  }, [module, translate]);

  useEffect(
    () => {
      getDocHeaderById(headerParams)
        .then(({ data }) => {
          // Jeigu narsykles virsuje pataiso operacijos numerii neegzistuojanti ir gauna tuscia atsakyma is API
          if (data.length === 0) {
            setLoader(false);
            setError({
              error: true,
              message: translate("invalidOpNo"),
              status: 0,
            });
            return;
          }

          setHeaderData(data[0]);
        })
        .then(() => {
          setHeaderReady(true);
        })
        .catch((reason) => {
          setLoader(false);
          const errMessage = getErrorMessage(reason);
          setError({
            error: true,
            message: errMessage.message,
            status: errMessage.status,
          });
        });
    }, // eslint-disable-next-line
    [data, refresh]
  );

  const handleClose = () => {
    logoutUser(error.status);

    if (error.status === 0) {
      navigate(-1);
    } else if (error.status !== 401) {
      setError({ error: false, message: "", status: 0 });
    }
  };

  const showError = (
    <Alert
      isOpen={error.error}
      message={error.message}
      iconColor="error"
      onClose={handleClose}
    />
  );

  const updateData = React.useCallback((updatedData: any) => {
    setData(updatedData);
  }, []);

  useEffect(() => {
    if (paramsReady && headerReady && dataReady) {
      setLoader(false);
    } else setLoader(true);
  }, [paramsReady, headerReady, dataReady]);

  React.useEffect(
    () => {
      const queryParams = {
        locale: locale,
        clientDbId: userDbId,
        module: module,
        operationCode: id,
      };

      getModuleParams(queryParams)
        .then(({ data }) => {
          if (data && data.length > 0) {
            dispatch(setUserExtraFieldsArray(data));

            const separator = data.filter(
              (sep: any) =>
                sep.parameter && sep.parameter.toUpperCase() === "SEPARATOR"
            );

            if (separator.length) {
              dispatch(setUserBarcodeSeparator(separator[0].code));
            } else dispatch(setUserBarcodeSeparator(";"));

            //delete rows
            const canDelete = data.find(
              (el: any) =>
                el.parameter && el.parameter.toUpperCase() === "DELETE"
            );

            if (canDelete && canDelete.code) {
              canDelete.code.toUpperCase() === ".T."
                ? dispatch(setUserRowDeletion(true))
                : dispatch(setUserRowDeletion(false));
            }

            //fields order
            const fieldsOrder = data.find(
              (el: any) =>
                el.parameter && el.parameter.toUpperCase() === "FIELDSORDER"
            );

            if (fieldsOrder && fieldsOrder.code) {
              dispatch(setUserFieldsOrder(fieldsOrder.code));
            }

            //search barcode
            const searchBarcode = data.find(
              (el: any) =>
                el.parameter && el.parameter.toUpperCase() === "BARCODESEARCH"
            );

            if (searchBarcode && searchBarcode.code) {
              const code = searchBarcode.code.toUpperCase();
              if (code === "P" || code === "S" || code === "N")
                dispatch(setUserBarcodeSearch(code));
            }

            //search OBJECT
            const searchObject = data.find(
              (el: any) =>
                el.parameter && el.parameter.toUpperCase() === "OBJECTSEARCH"
            );

            if (searchObject && searchObject.code) {
              const code = searchObject.code.toUpperCase();
              if (code === "P" || code === "S" || code === "N")
                dispatch(setUserObjectSearch(code));
            }

             //search LOCATION
             const searchLocation = data.find(
              (el: any) =>
                el.parameter && el.parameter.toUpperCase() === "LOCATIONSEARCH"
            );

            if (searchLocation && searchLocation.code) {
              const code = searchLocation.code.toUpperCase();
              if (code === "P" || code === "S" || code === "N")
                dispatch(setUserLocationSearch(code));
            }

            //search LOT
            const searchLot = data.find(
              (el: any) =>
                el.parameter && el.parameter.toUpperCase() === "LOTSEARCH"
            );

            if (searchLot && searchLot.code) {
              const code = searchLot.code.toUpperCase();
              if (code === "P" || code === "S" || code === "N")
                dispatch(setUserLotSearch(code));
            }

            //search OBJECT 2
            const searchObject2 = data.find(
              (el: any) =>
                el.parameter && el.parameter.toUpperCase() === "OBJECT2SEARCH"
            );

            if (searchObject2 && searchObject2.code) {
              const code = searchObject2.code.toUpperCase();
              if (code === "P" || code === "S" || code === "N")
                dispatch(setUserObject2Search(code));
            }

            //search LOT 2
            const searchLot2 = data.find(
              (el: any) =>
                el.parameter && el.parameter.toUpperCase() === "LOT2SEARCH"
            );

            if (searchLot2 && searchLot2.code) {
              const code = searchLot2.code.toUpperCase();
              if (code === "P" || code === "S" || code === "N")
                dispatch(setUserLot2Search(code));
            }
          }
        })
        .then(() => {
          setParamsReady(true);
        })
        .catch(() => {
          setLoader(false);
        });
    }, // eslint-disable-next-line
    []
  );

  const handleTabChange = (e: React.SyntheticEvent, value: any) => {
    setTab(value);
  };

  const handleFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
  };

  React.useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
  }, [tab]);

  const handleFocus = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.target.select();
  };

  const handleRefresh = useCallback(() => {
    dispatch(setUserRefreshData(!refresh));
  }, [refresh]);

  return (
    <BaseLayout
      moduleName={moduleName.name}
      modulePath={moduleName.path}
      button={{
        btnIcon: <Check />,
        btnText: translate("complete", "Užbaigti"),
        btnLink: routes.ROUTE_COMPLETION.path + "/" + module + "/" + id,
      }}
    >
      {error.error && showError}

      {useSearch && (
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          className={classes.container}
        >
          <Tab icon={<QrCodeScannerOutlined fontSize="small" />} />
          <Tab
            icon={
              <Badge
                color="warning"
                variant="dot"
                invisible={filter === ""}
                overlap="circular"
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
              >
                <RestoreOutlined fontSize="small" />
              </Badge>
            }
          />
        </Tabs>
      )}

      <HeaderField
        data={headerData}
        link={headerRoute}
        headerColor="grey"
        module={module}
      />
      <Divider className={classes.divider} />

      {tab === 0 && headerData.operationCode && (
        <ScannerForm
          module={module}
          operationCode={headerData?.operationCode}
          operationType={headerData?.operationType}
          userDb={userDbId}
          updateData={updateData}
          onSubmit={updateDocDetails}
        />
      )}

      {tab === 1 && (
        <div className={classes.search}>
          <TextField
            onChange={handleFilter}
            startAdornment={<SearchOutlined fontSize="small" />}
            value={filter}
            ref={searchRef}
            placeholder={translate("toLookFor")}
            onFocus={handleFocus}
          />

          <IconButton onClick={handleRefresh}>
            <CachedOutlined fontSize="small" />
          </IconButton>
        </div>
      )}

      <InfiniteScrollList
        loadData={getDocDetails}
        loadDataparams={loadDataparams}
        externalLoader
        onDataReady={() => setDataReady(true)}
      />
    </BaseLayout>
  );
}

export default DocumentDetails;
